/**
 * editorShared.ts
 *
 * Shared utilities, types, constants, and components used by both
 * TextOverlayEditor and UnifiedImageEditor.
 *
 * Extracted to avoid duplication and ensure consistent behaviour
 * across both editing surfaces.
 */

// ─── Self-hosted fonts (GDPR-compliant, loaded on demand via @fontsource) ────
// Clean & Modern
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/700.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/400-italic.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/open-sans/700-italic.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/700.css';
import '@fontsource/work-sans/400.css';
import '@fontsource/work-sans/700.css';
import '@fontsource/source-sans-3/400.css';
import '@fontsource/source-sans-3/700.css';
// Bold Headlines
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/400-italic.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/oswald/400.css';
import '@fontsource/oswald/700.css';
import '@fontsource/bebas-neue/400.css';
import '@fontsource/raleway/400.css';
import '@fontsource/raleway/700.css';
import '@fontsource/archivo-black/400.css';
import '@fontsource/teko/400.css';
import '@fontsource/teko/700.css';
// Elegant & Luxury
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/400-italic.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/abril-fatface/400.css';
import '@fontsource/libre-baskerville/400.css';
import '@fontsource/libre-baskerville/700.css';
import '@fontsource/cormorant-garamond/400.css';
import '@fontsource/cormorant-garamond/700.css';
// Friendly & Rounded
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/700.css';
import '@fontsource/quicksand/400.css';
import '@fontsource/quicksand/700.css';
import '@fontsource/rubik/400.css';
import '@fontsource/rubik/700.css';
import '@fontsource/jost/400.css';
import '@fontsource/jost/700.css';
// Script & Handwritten
import '@fontsource/dancing-script/400.css';
import '@fontsource/dancing-script/700.css';
import '@fontsource/pacifico/400.css';
import '@fontsource/lobster/400.css';
import '@fontsource/amatic-sc/400.css';
import '@fontsource/amatic-sc/700.css';

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { DEFAULT_EFFECT_PARAMS, type EffectId, type EffectParams } from './effects/presets';

// ─── Types ───────────────────────────────────────────────────────────────────

export type BgShape = 'none' | 'rect' | 'pill';

export interface TextLayer {
  id: string;
  text: string;
  x: number;         // display-space px (semantic: text content top-left, no canvas padding)
  y: number;
  width: number;     // text box width in display-space
  fontSize: number;  // display-space px
  fontFamily: string;
  fill: string;
  fontStyle: string; // 'normal' | 'bold' | 'italic' | 'bold italic'
  align: 'left' | 'center' | 'right';
  opacity: number;   // 0..1
  rotation: number;  // degrees
  effect: EffectId;
  effectParams: EffectParams;
  letterSpacing: number;  // display-space px (Konva native prop)
  lineHeight: number;     // multiplier (e.g. 1.0, 1.3, 1.6)
  bgShape: BgShape;       // background shape behind text
  bgColor: string;        // e.g. '#000000'
  bgOpacity: number;      // 0..1
  bgPadding: number;      // display-space px padding around text
}

/** Raw overlay hint from LLM (fill computed by Pillow post-generation). */
export interface OverlayHint {
  text: string;
  fontFamily: string;
  align: 'left' | 'center' | 'right';
  xFrac: number;
  yFrac: number;
  widthFrac?: number;
  fontSizePx: number;  // at original image resolution
  fill: string;
  fontStyle?: string;
}

// ─── History reducer ─────────────────────────────────────────────────────────

export type LayerAction =
  | { type: 'ADD_LAYER';    layer: TextLayer }
  | { type: 'DELETE_LAYER'; id: string }
  | { type: 'UPDATE_LAYER'; id: string; updates: Partial<TextLayer> }
  | { type: 'MOVE_LAYER';   id: string; direction: 'forward' | 'back' }
  | { type: 'REPLACE_ALL';  layers: TextLayer[] }  // pushes to history (template apply)
  | { type: 'RESIZE_ALL';   layers: TextLayer[] }  // does NOT push to history
  | { type: 'UNDO' }
  | { type: 'REDO' };

export interface LayerHistory {
  past: TextLayer[][];
  present: TextLayer[];
  future: TextLayer[][];
}

const MAX_HISTORY = 50;

export function layersReducer(state: LayerHistory, action: LayerAction): LayerHistory {
  const { past, present, future } = state;

  if (action.type === 'UNDO') {
    if (past.length === 0) return state;
    return { past: past.slice(0, -1), present: past[past.length - 1], future: [present, ...future] };
  }
  if (action.type === 'REDO') {
    if (future.length === 0) return state;
    return { past: [...past, present], present: future[0], future: future.slice(1) };
  }
  if (action.type === 'RESIZE_ALL') {
    return { ...state, present: action.layers }; // no history entry
  }

  let next: TextLayer[];

  switch (action.type) {
    case 'ADD_LAYER':
      next = [...present, action.layer];
      break;

    case 'DELETE_LAYER':
      next = present.filter(l => l.id !== action.id);
      break;

    case 'UPDATE_LAYER':
      next = present.map(l => l.id === action.id ? { ...l, ...action.updates } : l);
      break;

    case 'MOVE_LAYER': {
      const idx = present.findIndex(l => l.id === action.id);
      if (idx === -1) return state;
      const arr = [...present];
      if (action.direction === 'forward' && idx < arr.length - 1) {
        [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      } else if (action.direction === 'back' && idx > 0) {
        [arr[idx], arr[idx - 1]] = [arr[idx - 1], arr[idx]];
      } else {
        return state;
      }
      next = arr;
      break;
    }

    case 'REPLACE_ALL':
      next = action.layers;
      break;

    default:
      return state;
  }

  if (next === present) return state;
  return {
    past: [...past.slice(-MAX_HISTORY + 1), present],
    present: next,
    future: [],
  };
}

// ─── Font constants ──────────────────────────────────────────────────────────

export interface FontOption { label: string; value: string; }
export interface FontCategory { category: string; fonts: FontOption[]; }

export const FONT_CATEGORIES: FontCategory[] = [
  {
    category: 'Clean & Modern',
    fonts: [
      { label: 'Roboto',      value: 'Roboto' },
      { label: 'Inter',       value: 'Inter' },
      { label: 'Open Sans',   value: 'Open Sans' },
      { label: 'Lato',        value: 'Lato' },
      { label: 'Poppins',     value: 'Poppins' },
      { label: 'Work Sans',   value: 'Work Sans' },
      { label: 'Source Sans',  value: 'Source Sans 3' },
      { label: 'Arial',       value: 'Arial' },
    ],
  },
  {
    category: 'Bold Headlines',
    fonts: [
      { label: 'Montserrat',    value: 'Montserrat' },
      { label: 'Oswald',        value: 'Oswald' },
      { label: 'Bebas Neue',    value: 'Bebas Neue' },
      { label: 'Raleway',       value: 'Raleway' },
      { label: 'Archivo Black', value: 'Archivo Black' },
      { label: 'Teko',          value: 'Teko' },
      { label: 'Impact',        value: 'Impact' },
    ],
  },
  {
    category: 'Elegant & Luxury',
    fonts: [
      { label: 'Playfair Display',    value: 'Playfair Display' },
      { label: 'Abril Fatface',       value: 'Abril Fatface' },
      { label: 'Libre Baskerville',   value: 'Libre Baskerville' },
      { label: 'Cormorant Garamond',  value: 'Cormorant Garamond' },
      { label: 'Georgia',             value: 'Georgia' },
    ],
  },
  {
    category: 'Friendly & Rounded',
    fonts: [
      { label: 'Nunito',     value: 'Nunito' },
      { label: 'Quicksand',  value: 'Quicksand' },
      { label: 'Rubik',      value: 'Rubik' },
      { label: 'Jost',       value: 'Jost' },
    ],
  },
  {
    category: 'Script & Handwritten',
    fonts: [
      { label: 'Dancing Script', value: 'Dancing Script' },
      { label: 'Pacifico',       value: 'Pacifico' },
      { label: 'Lobster',        value: 'Lobster' },
      { label: 'Amatic SC',      value: 'Amatic SC' },
    ],
  },
  {
    category: 'Monospace',
    fonts: [
      { label: 'Courier',  value: 'Courier New' },
    ],
  },
];

// Flat list for lookups
export const ALL_FONTS: FontOption[] = FONT_CATEGORIES.flatMap(c => c.fonts);

// ─── Default layer ───────────────────────────────────────────────────────────

export const DEFAULT_LAYER_BASE: Omit<TextLayer, 'id' | 'x' | 'y' | 'width'> = {
  text: 'Your Text Here',
  fontSize: 48,
  fontFamily: 'Arial',
  fill: '#ffffff',
  fontStyle: 'bold',
  align: 'center',
  opacity: 1,
  rotation: 0,
  effect: 'none',
  effectParams: DEFAULT_EFFECT_PARAMS,
  letterSpacing: 0,
  lineHeight: 1.3,
  bgShape: 'none',
  bgColor: '#000000',
  bgOpacity: 0.6,
  bgPadding: 12,
};

let idCounter = 0;
export const nextId = () => `tl_${Date.now()}_${++idCounter}`;

// ─── Font Picker with categorized dropdown ───────────────────────────────────

export const FontPicker: React.FC<{
  value: string;
  onChange: (fontFamily: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const currentLabel = ALL_FONTS.find(f => f.value === value)?.label ?? value;

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs text-ink-2 mb-1">Font</label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-sm border border-line
                   rounded-lg px-2 py-1.5 bg-card
                   text-ink focus:ring-2 focus:ring-primary focus:outline-none
                   flex items-center justify-between gap-1 text-left"
        style={{ fontFamily: value }}
      >
        <span className="truncate">{currentLabel}</span>
        <ChevronDown size={14} className={`shrink-0 text-ink-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto
                     bg-card border border-line
                     rounded-lg shadow-xl"
        >
          {FONT_CATEGORIES.map(cat => (
            <div key={cat.category}>
              <div className="sticky top-0 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider
                              text-ink-3 bg-ground border-b border-line">
                {cat.category}
              </div>
              {cat.fonts.map(f => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => { onChange(f.value); setOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between
                    ${f.value === value
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-ink hover:bg-inset'}`}
                  style={{ fontFamily: f.value }}
                >
                  <span>{f.label}</span>
                  {f.value === value && <Check size={13} className="shrink-0 text-primary" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
